import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SuccessOverlayProps {
  goalName: string;
  isCustomGoal?: boolean;
  onDone: () => void;
}

export const SuccessOverlay = ({ goalName, isCustomGoal, onDone }: SuccessOverlayProps) => {
  // For custom goals, show a simpler message
  const headlineText = isCustomGoal 
    ? "You just stocked your fridge!"
    : `You just stocked your fridge for ${goalName}.`;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-background z-50 flex flex-col items-center justify-center p-8"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
        className="w-24 h-24 rounded-full gradient-primary flex items-center justify-center mb-8 shadow-glow"
      >
        <Check className="w-12 h-12 text-primary-foreground" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="text-center"
      >
        <div className="flex items-center justify-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-metabolic-400" />
          <span className="text-sm font-medium text-metabolic-500">All done!</span>
          <Sparkles className="w-5 h-5 text-metabolic-400" />
        </div>
        
        <h1 className="text-2xl font-semibold text-foreground mb-3">
          {headlineText}
        </h1>
        <p className="text-muted-foreground">
          Good job taking care of your health.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="mt-12 w-full max-w-sm"
      >
        <Button
          onClick={onDone}
          className="w-full h-14 text-base font-medium gradient-primary hover:opacity-90 transition-opacity"
        >
          Done
        </Button>
      </motion.div>
    </motion.div>
  );
};
